/**
 * Modal — bẫy tiêu điểm, đóng bằng Esc, trả tiêu điểm về nút kích hoạt
 * (docs/08 §8.7). Dùng class `.modal-scrim` / `.modal` của design system.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';

export interface ModalProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Bọc nội dung trong <form> và gọi onSubmit khi gửi. */
  onSubmit?: () => void;
  /**
   * Gọi khi BẤT KỲ ô nhập nào bên trong thay đổi (OQ-223).
   *
   * Sự kiện `change` nổi bọt trong React, nên một handler ở đây phủ mọi
   * `<input>`, `<select>`, `<textarea>` — kể cả ô thêm sau này mà không ai phải
   * nhớ. Dùng để xoá thông báo lỗi của máy chủ: nó là phán quyết về **giá trị
   * cũ**, giữ lại sau khi người dùng gõ khác đi là nói một điều chưa ai kiểm.
   */
  onFieldChange?: () => void;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  wide,
  onSubmit,
  onFieldChange,
}: ModalProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  /*
   * `onClose` giữ trong ref, KHÔNG nằm trong deps của effect dưới — OQ-143.
   *
   * Mọi nơi gọi đều truyền arrow nội tuyến (`onClose={() => setDialog(...)}`),
   * nên định danh của nó đổi mỗi lần thành phần cha vẽ lại. Để nó trong deps thì
   * mỗi lượt vẽ lại của cha sẽ **dọn rồi chạy lại** effect: `restoreRef` bị ghi
   * đè bằng chính nút trong modal, và `first.focus()` **giật tiêu điểm** khỏi ô
   * người dùng đang gõ, ném về nút Đóng.
   *
   * Đã đo trong jsdom trước khi sửa: đặt tiêu điểm ở ô nhập thứ hai, ép cha vẽ
   * lại một lần → `activeElement` thành `button[Đóng]`.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;

    const first = boxRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const box = boxRef.current;
      if (!box) return;
      const nodes = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (nodes.length === 0) return;
      const firstNode = nodes[0]!;
      const lastNode = nodes[nodes.length - 1]!;
      const active = document.activeElement;

      /*
       * Tiêu điểm KHÔNG còn trong hộp thì kéo nó về — OQ-143.
       *
       * Bản trước chỉ so `active` với phần tử đầu và cuối, nên chỉ bẫy đúng hai
       * điểm ấy. Bấm vào chỗ không nhận tiêu điểm trong modal (tiêu đề, một dòng
       * chữ) làm `activeElement` rơi về `body`: không khớp nhánh nào, `Tab` chạy
       * theo mặc định của trình duyệt, và vì modal dựng bằng portal ở **cuối**
       * `body` nên thứ tự tự nhiên đưa tiêu điểm vào đúng trang nằm **sau lưng
       * lớp phủ**. Đã đo: một `Tab` sau khi bấm tiêu đề → nút của trang nền.
       */
      if (!box.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? lastNode : firstNode).focus();
        return;
      }

      if (event.shiftKey && active === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && active === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      restoreRef.current?.focus();
    };
  }, [open]);

  const t = useT();
  if (!open) return null;

  const body = (
    <>
      <header>
        <div className="grow">
          <h2 id={titleId}>{title}</h2>
          {subtitle ? <div className="sub">{subtitle}</div> : null}
        </div>
        <button className="icon-btn" type="button" onClick={onClose} aria-label={t('common.close')}>
          <Icon name="close" size={17} />
        </button>
      </header>
      <div className="body">{children}</div>
      {footer ? <footer>{footer}</footer> : null}
    </>
  );

  return createPortal(
    <div
      className="modal-scrim"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {onSubmit ? (
        <form
          className={wide ? 'modal wide' : 'modal'}
          onChange={onFieldChange}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          ref={boxRef as unknown as React.RefObject<HTMLFormElement>}
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          {body}
        </form>
      ) : (
        <div
          className={wide ? 'modal wide' : 'modal'}
          onChange={onFieldChange}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          ref={boxRef}
        >
          {body}
        </div>
      )}
    </div>,
    document.body,
  );
}

/**
 * Hộp xác nhận nguy hiểm — buộc gõ đúng tên trước khi cho phép
 * (docs/08 §8.4 ConfirmDialog).
 */
export function ConfirmName({
  value,
  expected,
  onChange,
  label,
}: {
  value: string;
  expected: string;
  onChange: (next: string) => void;
  label: string;
}) {
  const id = useId();
  return (
    <label className="field" htmlFor={id}>
      <span>
        {label} <b className="mono">{expected}</b>
      </span>
      <input
        id={id}
        type="text"
        value={value}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
